import React, { useState, useRef, useEffect } from 'react';
import { FiCalendar, FiChevronDown, FiCheck, FiChevronLeft } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';

export interface DateRange {
  preset: 'Any date' | 'Today' | 'This week' | 'This month' | 'Custom';
  startDate?: string;
  endDate?: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(value.preset === 'Custom');
  const [startDate, setStartDate] = useState(value.startDate || '');
  const [endDate, setEndDate] = useState(value.endDate || '');

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowCustomInputs(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleStartDateChange = (newStart: string) => {
    setStartDate(newStart);
    // If end date is set and is prior to new start date, update end date to match start date
    if (endDate && newStart && endDate < newStart) {
      setEndDate(newStart);
    }
  };

  const handleEndDateChange = (newEnd: string) => {
    // End date cannot be prior to start date
    if (startDate && newEnd && newEnd < startDate) {
      return;
    }
    setEndDate(newEnd);
  };

  const handlePresetClick = (preset: DateRange['preset']) => {
    if (preset === 'Custom') {
      setShowCustomInputs(true);
      return;
    }
    setShowCustomInputs(false);
    setIsOpen(false);
    onChange({ preset });
  };

  const isInvalidRange = !startDate || !endDate || startDate > endDate;

  const handleApplyCustomRange = () => {
    if (isInvalidRange) return;
    setIsOpen(false);
    setShowCustomInputs(false);
    onChange({
      preset: 'Custom',
      startDate,
      endDate,
    });
  };

  const getLabel = () => {
    if (value.preset === 'Custom' && value.startDate && value.endDate) {
      return `${value.startDate} - ${value.endDate}`;
    }
    if (value.preset !== 'Any date') {
      return value.preset;
    }
    return 'Date';
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#13151a] px-4 py-2 text-sm font-medium text-zinc-200 transition hover:border-white/25 hover:bg-white/5 cursor-pointer"
      >
        <FiCalendar size={16} className="text-[#3B82F6]" />
        <span>{getLabel()}</span>
        <FiChevronDown size={14} className="text-zinc-400" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 sm:left-auto sm:right-0 top-full z-30 mt-3 w-[min(280px,calc(100vw-2rem))] rounded-3xl border border-white/15 bg-[#0E1015] p-3 shadow-2xl backdrop-blur-xl"
          >
            {!showCustomInputs ? (
              <div className="space-y-1 max-h-60 overflow-y-auto no-scrollbar">
                {(['Any date', 'Today', 'This week', 'This month', 'Custom'] as const).map(
                  (preset) => (
                    <button
                      key={preset}
                      onClick={() => handlePresetClick(preset)}
                      className="flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-sm text-zinc-200 hover:bg-white/5 transition"
                    >
                      <span>{preset === 'Custom' ? 'Custom Date Range...' : preset}</span>
                      {value.preset === preset && <FiCheck size={16} className="text-[#1784FF]" />}
                    </button>
                  ),
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => setShowCustomInputs(false)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white transition"
                >
                  <FiChevronLeft size={16} />
                  <span>Back to presets</span>
                </button>

                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      max={endDate || undefined}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      className="w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-xs text-white outline-none focus:border-white/20 [color-scheme:dark]"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate || undefined}
                      onChange={(e) => handleEndDateChange(e.target.value)}
                      className="w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-xs text-white outline-none focus:border-white/20 [color-scheme:dark]"
                    />
                  </div>
                </div>

                {startDate && endDate && startDate > endDate && (
                  <p className="text-[11px] font-medium text-red-400">
                    End date must be on or after start date.
                  </p>
                )}

                <button
                  onClick={handleApplyCustomRange}
                  disabled={isInvalidRange}
                  className="w-full rounded-2xl bg-[#1784FF] py-2.5 text-xs font-bold text-white transition hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Apply Range
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
